import { useEffect, useState } from 'react';
import { Button, CircularProgress, type SxProps, type Theme } from '@mui/material';
import { T } from '../../util/Localize';
import { startGoogleSignIn, type GoogleSsoIntent } from '../../services/google-sso';

/**
 * Google's standard multicolour "G" mark, in Google's official colours. Decorative: the button's
 * accessible name comes from its visible label alone.
 */
export function GoogleGMark({ size = 20 }: { size?: number }) {
    return (
        <svg
            data-testid="google-g-mark"
            aria-hidden="true"
            focusable="false"
            width={size}
            height={size}
            viewBox="0 0 48 48"
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: 'block' }}
        >
            <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            />
            <path
                fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            />
            <path
                fill="#FBBC05"
                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            />
            <path
                fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            />
        </svg>
    );
}

/**
 * Google's light-theme sign-in button colours. Set explicitly — including hover, focus and
 * disabled — so the brand theme's `MuiButton` overrides (`--button-outline-*` variables in
 * `theme-provider.tsx`) can never recolour it: the ticket requires white regardless of brand.
 */
const GOOGLE_BUTTON_SX: SxProps<Theme> = {
    py: 1,
    gap: 0.5,
    textTransform: 'none',
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 500,
    fontSize: 14,
    letterSpacing: '0.25px',
    color: '#1F1F1F',
    background: '#FFFFFF',
    border: '1px solid #747775',
    boxShadow: 'none',
    '&:hover': {
        color: '#1F1F1F',
        background: '#F2F2F2',
        borderColor: '#747775',
        boxShadow: 'none'
    },
    '&.Mui-focusVisible': {
        background: '#E8E8E8',
        borderColor: '#747775'
    },
    '&.Mui-disabled': {
        color: 'rgba(31, 31, 31, 0.38)',
        background: '#FFFFFF',
        borderColor: 'rgba(31, 31, 31, 0.12)'
    },
    '&.Mui-disabled .MuiButton-startIcon': {
        opacity: 0.38
    },
    '& .MuiButton-startIcon': {
        mr: 1.25,
        ml: 0
    }
};

export interface GoogleSignInButtonProps {
    /** Visible label and accessible name. UX-803 reuses this with "Sign up with Google". */
    label?: string;
    /** `'signin'` (default, login page) or `'signup'` (registration page) — decides where Google returns. */
    intent?: GoogleSsoIntent;
    /** E.g. while the password login is authorizing. */
    disabled?: boolean;
    /** Fired as the redirect starts, so the page can clear stale errors. */
    onRedirectStart?: () => void;
    /** Fired when the start URL could not be resolved, so the page can show the Google error. */
    onRedirectError?: () => void;
    /** Come back to this page after a successful sign-in (`startGoogleSignIn`'s `resumeTo`). */
    resumeTo?: string;
}

/**
 * Starts the API-driven Google round trip (see `services/google-sso.ts`) with a full navigation.
 *
 * Shows progress from the click onwards, because resolving the host brand and leaving the page
 * both take a moment; further clicks are ignored meanwhile. The spinner is dropped again if the
 * page comes back from the back/forward cache, otherwise Back from Google's screen would leave a
 * button that spins forever.
 */
export function GoogleSignInButton({
    label,
    intent = 'signin',
    disabled = false,
    onRedirectStart,
    onRedirectError,
    resumeTo
}: GoogleSignInButtonProps) {
    const [isRedirecting, setIsRedirecting] = useState(false);

    useEffect(() => {
        const handlePageShow = (e: PageTransitionEvent) => {
            if (e.persisted) setIsRedirecting(false);
        };
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, []);

    const handleClick = async () => {
        if (isRedirecting || disabled) return;
        setIsRedirecting(true);
        onRedirectStart?.();
        try {
            await startGoogleSignIn({ intent, resumeTo });
        } catch {
            // The host lookup that resolves the brand can reject; without this the button would
            // spin forever with no error and an unhandled rejection.
            setIsRedirecting(false);
            onRedirectError?.();
        }
    };

    return (
        <Button
            type="button"
            variant="outlined"
            fullWidth
            disableElevation
            disabled={disabled}
            aria-busy={isRedirecting || undefined}
            onClick={handleClick}
            startIcon={
                isRedirecting ? (
                    <CircularProgress
                        data-testid="google-sign-in-progress"
                        aria-hidden="true"
                        size={20}
                        sx={{ color: '#747775', display: 'block' }}
                    />
                ) : (
                    <GoogleGMark />
                )
            }
            sx={GOOGLE_BUTTON_SX}
        >
            {label ?? T('Sign in with Google')}
        </Button>
    );
}
